/**
 * Botão de ajuda (?) reutilizável do painel Pipeline KB.
 * Abre um Dialog com documentação estruturada da seção correspondente.
 */
import { useState } from "react";
import { HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import {
  KB_HELP_CONTENT,
  type KbHelpTopic,
  type HelpSection,
} from "./kb-help-content";

interface KbHelpButtonProps {
  topic: KbHelpTopic;
  size?: "xs" | "sm";
  className?: string;
  label?: string;
}

export function KbHelpButton({
  topic,
  size = "sm",
  className,
  label,
}: KbHelpButtonProps) {
  const [open, setOpen] = useState(false);
  const doc = KB_HELP_CONTENT[topic];
  const Icon = doc.icon;

  const dimensions =
    size === "xs" ? "h-6 w-6" : "h-7 w-7";
  const iconSize = size === "xs" ? "h-3.5 w-3.5" : "h-4 w-4";

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className={cn(
          dimensions,
          "text-muted-foreground hover:text-foreground rounded-full",
          className,
        )}
        title={label ?? "Ajuda desta seção"}
        aria-label={label ?? "Ajuda"}
      >
        <HelpCircle className={iconSize} />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 py-4 border-b space-y-2">
            <DialogTitle className="flex items-center gap-2.5 text-base">
              <span className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-muted">
                <Icon className="h-4 w-4" />
              </span>
              {doc.title}
            </DialogTitle>
            <DialogDescription className="text-xs leading-relaxed">
              {doc.subtitle}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            {doc.sections.map((section, idx) => (
              <SectionRenderer key={idx} section={section} />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Renderizador por tipo de seção
// ---------------------------------------------------------------------------
function SectionRenderer({ section }: { section: HelpSection }) {
  switch (section.kind) {
    case "heading":
      return (
        <h3 className="text-[13px] font-semibold tracking-tight text-foreground mt-2 pb-1 border-b border-border/60">
          {section.text}
        </h3>
      );

    case "paragraph":
      return (
        <p className="text-xs leading-relaxed text-muted-foreground">
          {section.text}
        </p>
      );

    case "list":
      return (
        <ul className="text-xs leading-relaxed text-muted-foreground space-y-1 pl-4 list-disc marker:text-muted-foreground/60">
          {section.items.map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </ul>
      );

    case "table":
      return (
        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                {section.columns.map((c) => (
                  <TableHead
                    key={c}
                    className="text-[11px] uppercase tracking-wide font-medium h-8"
                  >
                    {c}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {section.rows.map((row, i) => (
                <TableRow key={i}>
                  {row.map((cell, j) => (
                    <TableCell
                      key={j}
                      className={cn(
                        "text-xs align-top leading-relaxed",
                        j === 0 && "font-mono text-foreground whitespace-nowrap",
                        j !== 0 && "text-muted-foreground",
                      )}
                    >
                      {cell}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      );

    case "code":
      return (
        <div className="space-y-1">
          {section.label && (
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
              {section.label}
            </div>
          )}
          <pre className="text-[11px] leading-relaxed bg-muted/50 border rounded-md px-3 py-2.5 overflow-x-auto font-mono">
            {section.code}
          </pre>
        </div>
      );

    case "callout": {
      const toneClass =
        section.tone === "warning"
          ? "border-l-amber-500"
          : section.tone === "success"
            ? "border-l-emerald-500"
            : "border-l-sky-500";
      return (
        <Alert
          className={cn(
            "border-l-4 bg-muted/30 border-y-0 border-r-0 rounded-none pl-4 py-2.5",
            toneClass,
          )}
        >
          <AlertTitle className="text-xs font-semibold mb-0.5">
            {section.title}
          </AlertTitle>
          <AlertDescription className="text-xs leading-relaxed text-muted-foreground">
            {section.body}
          </AlertDescription>
        </Alert>
      );
    }

    default:
      return null;
  }
}
